import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import { Sun, Moon, AlertTriangle, Lock, FileSpreadsheet } from "lucide-react";
import { getCustoNovasVendas, putCustoNovasVendasCelula } from "../../api";
import SideBarNavGestaoOrcamentaria from "../../componets/SideBarNavGestaoOrcamentaria";
import { KpiRow } from "../../components/dashboard/KpiCard";
import { CATEGORIAS_CUSTO_NOVAS_VENDAS } from "../../constants/custoNovasVendas";
import { formatCurrency, formatPercent } from "../../utils/orcamento";

// Paleta padrão da plataforma (mesma usada nas demais telas de Gestão Orçamentária),
// acrescida dos tons de vinho/bege pedidos no layout de referência da planilha.
const palette = (isLight) => ({
  bg: isLight ? "#F8F7FC" : "#0F0A1E",
  card: isLight ? "#FFFFFF" : "#1A1030",
  border: isLight ? "#E5E0F0" : "#2E2050",
  text: isLight ? "#1A1030" : "#EDE9FA",
  muted: isLight ? "#7C6FA0" : "#9585C0",
  brand: "#7C3AED",
  success: "#10B981",
  danger: "#EF4444",
  staticBg: isLight ? "#F0EDF7" : "#211A38",
  vinho: "#7A1F4B",
  vinhoText: "#FFFFFF",
  bege1: isLight ? "#FBF3E6" : "#241D12",
  bege2: isLight ? "#F4E9D4" : "#2E2517",
  totalBg: isLight ? "#EADCC0" : "#3A2E19",
});

const ANO_ATUAL = new Date().getFullYear();
const OPCOES_ANO = Array.from({ length: ANO_ATUAL + 2 - 2025 + 1 }, (_, i) => 2025 + i);

const MESES_COMPLETOS = [
  "JANEIRO", "FEVEREIRO", "MARÇO", "ABRIL", "MAIO", "JUNHO",
  "JULHO", "AGOSTO", "SETEMBRO", "OUTUBRO", "NOVEMBRO", "DEZEMBRO",
];

function categoriaLabel(codigo) {
  const cat = CATEGORIAS_CUSTO_NOVAS_VENDAS.find((c) => c.codigo === codigo);
  return cat ? `${cat.codigo} - ${cat.label}` : codigo;
}

// Recalcula localmente (custo da célula, Total e coluna Total) logo após uma edição de
// % Histórico, para resposta instantânea — a resposta autoritativa do PUT substitui isso
// em seguida. Mesma fórmula do backend: Valor Total Novas Vendas do mês × % Imposto ×
// (100 − % Margem Novas Vendas) × % Histórico do mês.
function recalcularOtimista(dados, categoriaCodigo, mes, novoPercentual) {
  if (!dados) return dados;
  const valorTotalMes = Number(dados.valor_total_novas_vendas_por_mes?.[mes] || 0);
  const custoTotalMes =
    valorTotalMes * (Number(dados.percent_imposto || 0) / 100) *
    ((100 - Number(dados.percent_margem_novas_vendas || 0)) / 100);

  const categorias = dados.categorias.map((c) => {
    if (c.codigo !== categoriaCodigo) return c;
    const percent_historico = { ...c.percent_historico, [mes]: novoPercentual };
    const meses = { ...c.meses, [mes]: custoTotalMes * (novoPercentual / 100) };
    const total_anual = Object.values(meses).reduce((acc, v) => acc + Number(v || 0), 0);
    return { ...c, meses, percent_historico, total_anual };
  });

  const total_por_mes = {};
  for (let m = 1; m <= 12; m++) {
    total_por_mes[m] = categorias.reduce((acc, c) => acc + Number(c.meses[m] || 0), 0);
  }
  const total_anual_geral = categorias.reduce((acc, c) => acc + c.total_anual, 0);
  return { ...dados, categorias, total_por_mes, total_anual_geral };
}

export default function CustoNovasVendas() {
  const [isLight, setIsLight] = useState(true);
  const C = palette(isLight);
  const navigate = useNavigate();

  const [ano, setAno] = useState(ANO_ATUAL+1);
  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState("");

  const [salvandoCelulas, setSalvandoCelulas] = useState(() => new Set());
  const [errosCelulas, setErrosCelulas] = useState({});

  async function carregar() {
    setLoading(true);
    setErro("");
    try {
      const resposta = await getCustoNovasVendas(ano);
      setDados(resposta);
    } catch (e) {
      setErro(e.message);
    } finally {
      setLoading(false);
    }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { carregar(); }, [ano]);

  async function salvarCelula(categoriaCodigo, mes, novoPercentual) {
    const key = `${categoriaCodigo}_${mes}`;
    setDados((prev) => recalcularOtimista(prev, categoriaCodigo, mes, novoPercentual));
    setSalvandoCelulas((s) => new Set(s).add(key));
    setErrosCelulas((e) => {
      if (!(key in e)) return e;
      const novo = { ...e };
      delete novo[key];
      return novo;
    });
    try {
      const atualizado = await putCustoNovasVendasCelula({
        ano_referencia: ano,
        categoria_codigo: categoriaCodigo,
        mes,
        percentual: novoPercentual,
      });
      setDados(atualizado);
    } catch (e) {
      setErrosCelulas((prev) => ({ ...prev, [key]: e.message || "Falha ao salvar" }));
    } finally {
      setSalvandoCelulas((s) => {
        const novo = new Set(s);
        novo.delete(key);
        return novo;
      });
    }
  }

  const maiorPesoHistorico = useMemo(() => {
    if (!dados || !dados.total_anual_geral) return null;
    return dados.categorias.reduce((max, c) => (c.total_anual > max.total_anual ? c : max), dados.categorias[0]);
  }, [dados]);

  const valorTotalNovasVendasAno = useMemo(() => {
    if (!dados) return 0;
    return Object.values(dados.valor_total_novas_vendas_por_mes || {}).reduce((acc, v) => acc + Number(v || 0), 0);
  }, [dados]);

  // ── Exportar para Excel (backup/consulta caso o BD apresente problema) ────
  function exportarParaExcel() {
    if (!dados) return;
    const linhas = [];
    const linhaBase = (label, valores, total) => ({
      "DRE Gerencial": label,
      ...Object.fromEntries(MESES_COMPLETOS.map((m, i) => [m, valores[i]])),
      "Total": total,
    });

    linhas.push(linhaBase(
      "Valor Total Novas Vendas",
      Array.from({ length: 12 }, (_, i) => dados.valor_total_novas_vendas_por_mes?.[i + 1] || 0),
      valorTotalNovasVendasAno
    ));
    linhas.push(linhaBase("% Imposto", Array(12).fill(formatPercent(dados.percent_imposto)), "—"));

    CATEGORIAS_CUSTO_NOVAS_VENDAS.forEach((cat) => {
      const categoria = dados.categorias.find((c) => c.codigo === cat.codigo);
      linhas.push(linhaBase(
        `${cat.codigo} - ${cat.label}`,
        Array.from({ length: 12 }, (_, i) => categoria?.meses?.[i + 1] || 0),
        categoria?.total_anual || 0
      ));
    });

    linhas.push(linhaBase(
      "Total",
      Array.from({ length: 12 }, (_, i) => dados.total_por_mes?.[i + 1] || 0),
      dados.total_anual_geral || 0
    ));
    linhas.push(linhaBase("% Margem Novas Vendas", Array(12).fill(formatPercent(dados.percent_margem_novas_vendas)), "—"));

    linhas.push({ "DRE Gerencial": "% Histórico" });
    CATEGORIAS_CUSTO_NOVAS_VENDAS.forEach((cat) => {
      const categoria = dados.categorias.find((c) => c.codigo === cat.codigo);
      linhas.push(linhaBase(
        `${cat.codigo} - ${cat.label}`,
        Array.from({ length: 12 }, (_, i) => formatPercent(categoria?.percent_historico?.[i + 1] || 0)),
        "—"
      ));
    });

    const worksheet = XLSX.utils.json_to_sheet(linhas);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Custo Novas Vendas".slice(0, 31));
    const nomeArquivo = `Custo_Novas_Vendas_${ano}`.replace(/[^\w]+/g, "_");
    XLSX.writeFile(workbook, `${nomeArquivo}.xlsx`);
  }

  const kpiItems = [
    { label: "Valor Total Novas Vendas no Ano", value: loading || !dados ? "—" : formatCurrency(valorTotalNovasVendasAno), color: C.text },
    { label: "Custo Total Projetado no Ano", value: loading || !dados ? "—" : formatCurrency(dados.total_anual_geral), color: C.brand },
    { label: "% Margem Novas Vendas", value: loading || !dados ? "—" : formatPercent(dados.percent_margem_novas_vendas), color: C.success },
    { label: "% Imposto", value: loading || !dados ? "—" : formatPercent(dados.percent_imposto), color: C.text },
    {
      label: "Categoria de Maior Peso no Custo",
      value: loading || !maiorPesoHistorico
        ? "—"
        : `${categoriaLabel(maiorPesoHistorico.codigo)} · ${formatPercent((maiorPesoHistorico.total_anual / dados.total_anual_geral) * 100)}`,
      color: C.text,
    },
  ];

  return (
    <div style={{ display: "flex", height: "100vh", overflow: "hidden", background: C.bg, fontFamily: "'DM Sans', sans-serif", color: C.text }}>
      <SideBarNavGestaoOrcamentaria isLight={isLight} />

      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "auto" }}>
        <header style={{
          background: "linear-gradient(90deg, #4c1d95 0%, #9d174d 100%)",
          color: "#fff", padding: "14px 24px",
          display: "flex", justifyContent: "space-between", alignItems: "center",
          boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 22, fontWeight: 800 }}>YOUX</span>
            <span style={{ fontSize: 18, fontWeight: 300 }}>GROUP</span>
            <span style={{ fontSize: 13, fontWeight: 500, opacity: 0.8, marginLeft: 8 }}>· Custo Novas Vendas</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <select
              value={ano}
              onChange={(e) => setAno(parseInt(e.target.value, 10))}
              style={{ background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.25)", borderRadius: 8, padding: "7px 10px", color: "#fff", fontSize: 13, fontWeight: 600 }}
            >
              {OPCOES_ANO.map((a) => <option key={a} value={a} style={{ color: "#0F172A" }}>{a}</option>)}
            </select>
            <button onClick={() => setIsLight((l) => !l)} style={{
              display: "flex", alignItems: "center", gap: 6,
              background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.25)",
              borderRadius: 8, padding: "7px 12px", cursor: "pointer", color: "#fff", fontSize: 12, fontWeight: 600,
            }}>
              {isLight ? <Sun size={14} /> : <Moon size={14} />}
              {isLight ? "Tema Claro" : "Tema Escuro"}
            </button>
            <button
              onClick={() => navigate("/inicio")}
              style={{ display: "flex", alignItems: "center", gap: 6, background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.25)", borderRadius: 8, padding: "7px 14px", cursor: "pointer", color: "#fff", fontSize: 13, fontWeight: 600 }}
            >
              Menu Principal
            </button>
          </div>
        </header>

        <main style={{ padding: 24, display: "flex", flexDirection: "column", gap: 24 }}>
          {erro && (
            <div style={{
              display: "flex", alignItems: "center", gap: 8, background: `${C.danger}18`,
              border: `1px solid ${C.danger}`, color: C.danger, borderRadius: 10, padding: "10px 14px", fontSize: 13,
            }}>
              <AlertTriangle size={16} /> {erro}
            </div>
          )}

          <section style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <KpiRow items={kpiItems} C={C} />
          </section>

          <section>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, flexWrap: "wrap", gap: 12 }}>
              <h2 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: C.text }}>
                Custo Novas Vendas · {ano}
              </h2>
              <p style={{ margin: 0, fontSize: 11, color: C.muted, display: "flex", alignItems: "center", gap: 6 }}>
                <Lock size={12} /> A única tabela editável é "% Histórico" — o custo por categoria é preenchido automaticamente a partir dela.
              </p>
              <button
                onClick={exportarParaExcel}
                disabled={loading || !dados}
                title="Exportar dados para Excel"
                style={{ ...btnStyle(C.success, true), opacity: loading || !dados ? 0.45 : 1, cursor: loading || !dados ? "not-allowed" : "pointer" }}
              >
                <FileSpreadsheet size={15} /> Exportar Excel
              </button>
            </div>

            {loading ? (
              <div style={{ padding: 48, textAlign: "center", color: C.muted, fontSize: 13 }}>Carregando…</div>
            ) : !dados ? (
              <div style={{ padding: 48, textAlign: "center", color: C.muted, fontSize: 13, background: C.card, border: `1px solid ${C.border}`, borderRadius: 14 }}>
                Não foi possível carregar os dados de {ano}.
              </div>
            ) : (
              <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, overflow: "hidden" }}>
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, minWidth: 1600 }}>
                    <thead>
                      <tr>
                        <th style={thHeaderStyle(C, true)}>DRE GERENCIAL {ano}</th>
                        {MESES_COMPLETOS.map((m) => <th key={m} style={thHeaderStyle(C)}>{m}</th>)}
                        <th style={thHeaderStyle(C)}>TOTAL</th>
                      </tr>
                    </thead>
                    <tbody>
                      {/* Valor Total Novas Vendas — somente leitura, vem do módulo Orçamento > Novas Vendas */}
                      <tr>
                        <td style={{ ...tdLabelStyle(C), background: C.staticBg }} title="Vem do módulo Orçamento > Novas Vendas">Valor Total Novas Vendas</td>
                        {Array.from({ length: 12 }, (_, i) => i + 1).map((mes) => (
                          <td key={mes} style={{ ...tdStyle(C), background: C.staticBg, color: C.muted, cursor: "not-allowed" }} title="Vem do módulo Orçamento > Novas Vendas">
                            {formatCurrency(dados.valor_total_novas_vendas_por_mes?.[mes] || 0)}
                          </td>
                        ))}
                        <td style={{ ...tdStyle(C), background: C.staticBg, color: C.muted }}>
                          {formatCurrency(valorTotalNovasVendasAno)}
                        </td>
                      </tr>

                      {/* % Imposto — somente leitura, vem de Premissas de Faturamento */}
                      <tr>
                        <td style={{ ...tdLabelStyle(C), background: C.staticBg }} title="Editável em Premissas de Faturamento">% Imposto</td>
                        {MESES_COMPLETOS.map((_, i) => (
                          <td key={i} style={{ ...tdStyle(C), background: C.staticBg, color: C.muted, cursor: "not-allowed" }} title="Editável em Premissas de Faturamento">
                            {formatPercent(dados.percent_imposto)}
                          </td>
                        ))}
                        <td style={{ ...tdStyle(C), background: C.staticBg, color: C.muted }}>—</td>
                      </tr>

                      {/* Categorias — custo calculado automaticamente (Valor Total Novas Vendas × % Imposto ×
                          (100 − % Margem Novas Vendas) × % Histórico); somente leitura nesta tabela */}
                      {CATEGORIAS_CUSTO_NOVAS_VENDAS.map((cat, idx) => {
                        const categoria = dados.categorias.find((c) => c.codigo === cat.codigo);
                        const bgLinha = idx % 2 === 0 ? C.bege1 : C.bege2;
                        return (
                          <tr key={cat.codigo}>
                            <td style={{ ...tdLabelStyle(C), background: bgLinha }}>{cat.codigo} - {cat.label}</td>
                            {Array.from({ length: 12 }, (_, i) => i + 1).map((mes) => (
                              <td
                                key={mes}
                                style={{ ...tdStyle(C), background: bgLinha, color: C.muted, cursor: "not-allowed" }}
                                title="Calculado automaticamente a partir do % Histórico"
                              >
                                {formatCurrency(categoria?.meses?.[mes] || 0)}
                              </td>
                            ))}
                            <td style={{ ...tdStyle(C), background: bgLinha, fontWeight: 700 }}>
                              {formatCurrency(categoria?.total_anual || 0)}
                            </td>
                          </tr>
                        );
                      })}

                      {/* Total */}
                      <tr style={{ fontWeight: 800 }}>
                        <td style={{ ...tdLabelStyle(C), background: C.totalBg }}>Total</td>
                        {Array.from({ length: 12 }, (_, i) => i + 1).map((mes) => (
                          <td key={mes} style={{ ...tdStyle(C), background: C.totalBg }}>
                            {formatCurrency(dados.total_por_mes?.[mes] || 0)}
                          </td>
                        ))}
                        <td style={{ ...tdStyle(C), background: C.totalBg }}>{formatCurrency(dados.total_anual_geral || 0)}</td>
                      </tr>

                      <SpacerRow C={C} />

                      {/* % Margem Novas Vendas — somente leitura, vem de Premissas de Faturamento */}
                      <tr>
                        <td style={{ ...tdLabelStyle(C), background: C.staticBg }} title="Editável em Premissas de Faturamento">% Margem Novas Vendas</td>
                        {MESES_COMPLETOS.map((_, i) => (
                          <td key={i} style={{ ...tdStyle(C), background: C.staticBg, color: C.muted, cursor: "not-allowed" }} title="Editável em Premissas de Faturamento">
                            {formatPercent(dados.percent_margem_novas_vendas)}
                          </td>
                        ))}
                        <td style={{ ...tdStyle(C), background: C.staticBg, color: C.muted }}>—</td>
                      </tr>

                      <SpacerRow C={C} />

                      {/* % Histórico — única tabela editável; percentual de rateio do custo por categoria/mês */}
                      <tr>
                        <td colSpan={14} style={{
                          padding: "8px 10px", fontSize: 11, fontWeight: 700, color: C.muted,
                          textTransform: "uppercase", letterSpacing: "0.06em", background: C.bg,
                        }}>
                          % Histórico
                        </td>
                      </tr>
                      {CATEGORIAS_CUSTO_NOVAS_VENDAS.map((cat, idx) => {
                        const categoria = dados.categorias.find((c) => c.codigo === cat.codigo);
                        const bgLinha = idx % 2 === 0 ? C.bege1 : C.bege2;
                        return (
                          <tr key={`hist-${cat.codigo}`}>
                            <td style={{ ...tdLabelStyle(C), background: bgLinha }}>
                              {cat.codigo} - {cat.label}
                            </td>
                            {Array.from({ length: 12 }, (_, i) => i + 1).map((mes) => {
                              const key = `${cat.codigo}_${mes}`;
                              return (
                                <td key={mes} style={{ ...tdStyle(C), background: bgLinha, padding: "4px 6px" }}>
                                  <CelulaPercentEditavel
                                    C={C}
                                    valor={Number(categoria?.percent_historico?.[mes] || 0)}
                                    salvando={salvandoCelulas.has(key)}
                                    erro={errosCelulas[key]}
                                    onSalvar={(novoPercentual) => salvarCelula(cat.codigo, mes, novoPercentual)}
                                  />
                                </td>
                              );
                            })}
                            <td style={{ ...tdStyle(C), background: bgLinha, color: C.muted }}>—</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </section>
        </main>
      </div>
    </div>
  );
}

// ── Helpers de UI locais ─────────────────────────────────────────────────────

function CelulaPercentEditavel({ C, valor, salvando, erro, onSalvar }) {
  const [editando, setEditando] = useState(false);
  const [texto, setTexto] = useState(String(valor));

  useEffect(() => {
    if (!editando) setTexto(String(valor));
  }, [valor, editando]);

  function confirmar() {
    setEditando(false);
    const novoValor = Math.min(100, Math.max(0, parseFloat(String(texto).replace(",", ".")) || 0));
    if (novoValor !== Number(valor)) onSalvar(novoValor);
  }

  return (
    <input
      type="text"
      inputMode="decimal"
      value={editando ? texto : formatPercent(valor)}
      onFocus={() => { setEditando(true); setTexto(String(valor)); }}
      onChange={(e) => setTexto(e.target.value)}
      onBlur={confirmar}
      onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
      title={erro || undefined}
      style={{
        width: "100%", textAlign: "right", background: "transparent",
        border: erro ? `1px solid ${C.danger}` : (salvando ? `1px dashed ${C.brand}` : "1px solid transparent"),
        borderRadius: 4, padding: "5px 6px", fontSize: 12, color: erro ? C.danger : C.text,
        fontFamily: "inherit", outline: "none",
      }}
    />
  );
}

function SpacerRow({ C }) {
  return (
    <tr>
      <td colSpan={14} style={{ height: 10, padding: 0, border: "none", background: C.bg }} />
    </tr>
  );
}

function btnStyle(color, secondary = false) {
  return {
    display: "flex", alignItems: "center", gap: 6,
    background: secondary ? "transparent" : color,
    border: secondary ? `1px solid ${color}` : "none",
    color: secondary ? color : "#fff",
    borderRadius: 8, padding: "9px 16px", fontSize: 13, fontWeight: 700,
    fontFamily: "inherit", cursor: "pointer",
  };
}

function thHeaderStyle(C, alignLeft = false) {
  return {
    padding: "10px 10px", fontSize: 11, fontWeight: 700, color: C.vinhoText,
    textTransform: "uppercase", letterSpacing: "0.04em", textAlign: alignLeft ? "left" : "right",
    background: C.vinho, whiteSpace: "nowrap",
  };
}

function tdStyle(C) {
  return {
    padding: "8px 10px", fontSize: 12, color: C.text, textAlign: "right",
    borderBottom: `1px solid ${C.border}`, whiteSpace: "nowrap",
  };
}

function tdLabelStyle(C) {
  return {
    padding: "8px 10px", fontSize: 12, color: C.text, textAlign: "left",
    borderBottom: `1px solid ${C.border}`, whiteSpace: "nowrap", fontWeight: 600,
  };
}
